import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { useLang } from "@/context/LanguageContext";
import { LEGAL, lget } from "@/i18n/legal";

export default function LegalLayout({ pageKey }) {
  const { lang, t } = useLang();
  const page = LEGAL[pageKey];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pageKey]);

  if (!page) return null;

  const { meta, sections } = page;

  return (
    <div className="min-h-screen bg-white text-brand-ink font-body" data-testid={`legal-${pageKey}-page`}>
      <Header />
      <main className="pt-28 md:pt-36 pb-24 md:pb-32">
        <div className="container-x max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand-stone hover:text-brand-ink transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {lget(LEGAL.back, lang)}
          </Link>

          <span className="mt-8 overline block">{lget(meta.overline, lang)}</span>
          <h1 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
            {lget(meta.title, lang)}
          </h1>

          <p className="mt-6 text-base md:text-lg text-brand-graphite leading-relaxed text-pretty">
            {lget(meta.intro, lang)}
          </p>

          {meta.disclaimer && (
            <div className="mt-8 rounded-2xl border border-brand-champagne/60 bg-brand-champagneSoft/30 p-5 flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-brand-graphite shrink-0 mt-0.5" />
              <p className="text-sm text-brand-graphite leading-relaxed">
                {lget(meta.disclaimer, lang)}
              </p>
            </div>
          )}

          <div className="mt-14 space-y-10">
            {page.html ? (
              <div
                className="legal-prose"
                data-testid="legal-html-content"
                dangerouslySetInnerHTML={{ __html: lget(page.html, lang) }}
              />
            ) : (
              sections.map((s, i) => (
              <section key={i} data-testid={`legal-section-${i}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <h2 className="font-heading text-xl md:text-2xl font-medium text-brand-ink tracking-tight">
                    {lget(s.title, lang)}
                  </h2>
                  {s.placeholder && (
                    <span className="inline-flex items-center rounded-full border border-brand-champagne bg-white px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-brand-graphite">
                      {lget(LEGAL.placeholderBadge, lang)}
                    </span>
                  )}
                </div>
                <ul className="mt-4 space-y-2.5">
                  {s.items.map((it, j) => (
                    <li key={j} className="text-[15px] md:text-base text-brand-graphite leading-relaxed">
                      {lget(it, lang)}
                    </li>
                  ))}
                </ul>
              </section>
              ))
            )}
          </div>

          <div className="mt-16 pt-8 border-t border-brand-mist text-xs text-brand-stone">
            {lang === "de"
              ? `Stand: ${new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" })}. Diese Seite wurde zuletzt aktualisiert, als Sie sie geöffnet haben.`
              : `Last updated: ${new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}.`}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
